// RoleLayout — shell común para roles con sidebar (Coordinador, Supervisor,
// Admin tenant). Estructura del mockup screen-c5.jsx / sa-shared.jsx:
//
//   ┌─ Header 60px (wordmark + tenant + clock + bell + avatar + signOut)
//   ├─ Sidebar 240px ─┬─ Content area (padding 24px, gap 12-16px)
//   │                 │
//   │                 │
//   └─────────────────┴─
//
// En mobile el sidebar vive en un DRAWER (botón hamburguesa en el header): el
// supervisor camina la planta con el celular y necesita la misma navegación
// que en desktop (Tiendas, Incidencias, Histórico, ...).

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { AppHeader } from './AppHeader';
import { useDensity } from '../../lib/theme/density';
import { useAuth } from '../../lib/auth';
import { useVerComoActivo } from '../../lib/ver-como';

interface RoleLayoutProps {
  /** Sidebar component a usar (CoordinadorSidebar / SupervisorSidebar / ...). */
  sidebar: ReactNode;
  /** Etiqueta del rol en el header (ej. "Coordinador · UPG Sopó"). */
  roleLabel?: string;
  children: ReactNode;
  /** Si se pasa, contenido extra al lado de la info del user en el header. */
  headerExtra?: ReactNode;
}

export function RoleLayout({ sidebar, roleLabel, children, headerExtra }: RoleLayoutProps) {
  const { density } = useDensity();
  const showSidebar = density === 'desktop';
  const [menuOpen, setMenuOpen] = useState(false);

  // Coherencia del cruce de mundos (2026-06-11): cuando un DIRECTIVO entra a
  // pantallas con shell de coordinador/admin (drill-down o configuración), el
  // header mantiene su identidad ("… · Directivo") y aparece un camino de
  // vuelta al pulso. Sin esto, la plataforma se sentía como apps pegadas.
  // En modo "Ver como" el directivo suplanta a la persona real: el shell debe
  // verse EXACTAMENTE como el de ese rol (sin reetiquetar a "Directivo" ni
  // mostrar el "volver al pulso" — de eso se encarga el banner de suplantación).
  const { state } = useAuth();
  const verComoActivo = useVerComoActivo();
  const esDirectivo =
    state.kind === 'signed_in' && state.claims.rol === 'directivo' && !verComoActivo;
  const labelEfectivo =
    esDirectivo && roleLabel ? roleLabel.replace(/^Coordinador/, 'Directivo') : roleLabel;

  return (
    <div className="flex h-screen flex-col bg-surface-base text-fg">
      <AppHeader
        roleLabel={labelEfectivo}
        extra={headerExtra}
        onMenuToggle={showSidebar ? undefined : () => setMenuOpen(true)}
      />
      {/* Drawer mobile: backdrop + el MISMO sidebar del rol. Tocar un link o el
          fondo lo cierra. */}
      {!showSidebar && menuOpen ? (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="h-full w-64 overflow-y-auto border-r border-border bg-surface-base shadow-xl"
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest('a')) setMenuOpen(false);
            }}
          >
            {sidebar}
          </div>
          <button
            type="button"
            aria-label="Cerrar menú"
            className="flex-1 bg-black/50"
            onClick={() => setMenuOpen(false)}
          />
        </div>
      ) : null}
      {esDirectivo ? (
        <div className="flex items-center border-b border-border bg-surface-base px-4 py-1.5 md:px-6">
          <Link
            to="/directivo"
            className="text-xs font-medium text-fg-secondary transition-colors hover:text-fg"
          >
            ← Volver al pulso del día
          </Link>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1">
        {showSidebar ? sidebar : null}
        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-6 md:py-7">{children}</main>
      </div>
    </div>
  );
}

// Re-export para que pantallas que quieran un header consistente sin el shell
// completo puedan usarlo (ej. LoginPage tiene su propio layout custom).
export { Badge };
